import type { FC } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DashboardLoadingSkeleton } from "src/components/common";
import BackButton from "../Components/BackButton";
import ListeningReport from "../Components/ListeningReport";
import StatusMessage from "../Components/StatusMessage";
import { useListeningReport } from "../Hooks/UseListeningReport";
import { SOCIAL_LISTENING_ROUTES } from "../Utils/SocialListeningConstants";
import "../Styles/ListeningReport.scss";
import "../Styles/Shared.scss";

const STATE_KEY = "socialListingAgent.listeningReport.state";

const parseRunId = (value: string | null | undefined): number | null => {
  if (!value) {return null;}
  const runId = Number(value);
  return Number.isInteger(runId) && runId > 0 ? runId : null;
};

const BrandListeningReport: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { runId: runIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const rawRunId = runIdParam ?? searchParams.get("run_id");
  const runId = parseRunId(rawRunId);
  const { report, status, analysisType, isLoading, error, retry } = useListeningReport(runId);
  const startAgainRoute =
    analysisType === "competitive"
      ? SOCIAL_LISTENING_ROUTES.competitiveAnalysis
      : SOCIAL_LISTENING_ROUTES.brandAnalysis;

  const renderContent = () => {
    if (runId === null || error === "notFound") {
      return (
        <StatusMessage
          tone="error"
          title={t(`${STATE_KEY}.notFoundTitle`, "Report not found")}
          message={t(
            `${STATE_KEY}.notFound`,
            "This analysis doesn't exist, or you don't have access to it."
          )}
          action={{
            label: t(`${STATE_KEY}.viewAll`, "View all reports"),
            onClick: () => navigate(SOCIAL_LISTENING_ROUTES.history),
          }}
        />
      );
    }
    if (isLoading) {
      return <DashboardLoadingSkeleton />;
    }
    if (error === "loadFailed" && !status) {
      return (
        <StatusMessage
          tone="error"
          title={t(`${STATE_KEY}.loadFailedTitle`, "Report unavailable")}
          message={t(`${STATE_KEY}.loadFailed`, "We couldn't load this report. Please try again.")}
          action={{ label: t(`${STATE_KEY}.retry`, "Try again"), onClick: retry }}
        />
      );
    }
    if (status === "pending") {
      return (
        <StatusMessage
          tone="pending"
          title={t(`${STATE_KEY}.pendingTitle`, "Your report is being prepared")}
          message={t(
            `${STATE_KEY}.pending`,
            "This can take from a few minutes up to 2 hours. We'll email you when it's ready, and this page updates on its own."
          )}
        />
      );
    }
    if (status === "failed" || !report) {
      return (
        <StatusMessage
          tone="error"
          title={t(`${STATE_KEY}.failedTitle`, "This analysis failed")}
          message={t(
            `${STATE_KEY}.failed`,
            "We couldn't collect data for this analysis. Please start a new one."
          )}
          action={{
            label: t(`${STATE_KEY}.startAgain`, "Start a new analysis"),
            onClick: () => navigate(startAgainRoute),
          }}
        />
      );
    }
    return <ListeningReport report={report} autoDownload={searchParams.get("download") === "1"} />;
  };

  return (
    <>
      <div className="br-page-back">
        <BackButton label={t("socialListingAgent.reportPage.back")} />
      </div>
      {renderContent()}
    </>
  );
};

export default BrandListeningReport;
